import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Search, Users, UserCheck, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function MedicalPersonListPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDivision, setFilterDivision] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [divisions, setDivisions] = useState([]);

  const fetchPersons = async () => {
    try {
      const params = {};
      if (filterDivision) params.division = filterDivision;
      if (filterType) params.person_type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;

      const res = await axios.get(`${API}/medical/persons`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setPersons(res.data);
    } catch {
      toast.error("Failed to load persons");
    } finally {
      setLoading(false);
    }
  };

  const fetchDivisions = async () => {
    try {
      const res = await axios.get(`${API}/medical/divisions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDivisions(res.data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchPersons();
    fetchDivisions();
  }, [token, filterDivision, filterType, filterStatus]);

  useEffect(() => {
    const timer = setTimeout(() => fetchPersons(), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const getStatusBadge = (person) => {
    const mf = person.medical_form || {};
    if (!mf.has_form) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-300">
          Missing
        </span>
      );
    }
    if (mf.is_expired) {
      return (
        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
          Expired
        </span>
      );
    }
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-300">
        Current
      </span>
    );
  };

  // Group by division
  const grouped = {};
  persons.forEach((p) => {
    const div = p.division || "Other";
    if (!grouped[div]) grouped[div] = [];
    grouped[div].push(p);
  });

  return (
    <div className="space-y-6" data-testid="medical-person-list">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Medical Persons
          </h1>
          <p className="text-slate-500 mt-1">
            {persons.length} total — Staff & Campers
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="medical-search-input"
          />
        </div>
        <select
          value={filterDivision}
          onChange={(e) => setFilterDivision(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
          data-testid="medical-filter-division"
        >
          <option value="">All Divisions</option>
          {divisions.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
          data-testid="medical-filter-type"
        >
          <option value="">All Types</option>
          <option value="staff">Staff</option>
          <option value="camper">Camper</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
          data-testid="medical-filter-status"
        >
          <option value="">All Statuses</option>
          <option value="compliant">Current</option>
          <option value="expired">Expired</option>
          <option value="missing">Missing</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
        </div>
      ) : persons.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">
            No persons found. Sync CampMinder from the dashboard.
          </p>
        </div>
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([division, divPersons]) => (
            <div
              key={division}
              className="bg-white border border-slate-200 rounded-lg overflow-hidden"
              data-testid={`medical-division-${division.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-heading font-semibold text-slate-800">
                  {division}
                </h2>
                <span className="text-xs text-slate-500 font-medium">
                  {divPersons.length} person
                  {divPersons.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="divide-y divide-slate-100">
                {divPersons.map((person) => (
                  <div
                    key={person.id}
                    className="px-6 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() =>
                      navigate(`/medical/persons/${person.id}`)
                    }
                    data-testid={`medical-person-row-${person.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 text-xs font-bold">
                        {(person.first_name || "?")[0]}
                        {(person.last_name || "?")[0]}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {person.name}
                        </p>
                        <p className="text-xs text-slate-400">
                          {person.person_type === "camper"
                            ? "Camper"
                            : person.position || "Staff"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(person)}
                      {person.medical_form?.expiration_date && (
                        <span className="text-xs text-slate-400 hidden sm:inline">
                          Exp:{" "}
                          {new Date(
                            person.medical_form.expiration_date
                          ).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
      )}
    </div>
  );
}
